import React, { useEffect, useRef, useState } from "react";
import { EmptyState } from "../../components/ui";
import { Icon } from "../shared/components";
import ImportWhatsAppSection from "./ImportWhatsAppSection";
import {
  getInitialTheme, applyTheme, saveTheme,
  getInitialPrefs, updatePref, resetPrefs,
  ACCENT_PRESETS,
} from "../shared/theme";
import {
  ensureArray, formatTime,
  formatBrazilPhone, maskBrazilPhoneInput,
  signatureTemplatePresets, normalizeSignatureTemplate, formatSignaturePreview,
  parseTemplateVariables, formatTemplateVariables, renderTemplatePreview,
  renderQuickReplyBody,
  getInitials,
} from "../shared/utils";

// ── Avatar color — mesma paleta do sistema ────────────────────────────────────
const _AV = ['#25D366','#3B9DFF','#A78BFA','#F471B5','#F5A524','#34D2C8','#FF7849'];
function getAvatarColor(name) {
  if (!name) return _AV[0];
  const seed = [...String(name)].reduce((a, c) => a + c.charCodeAt(0), 0);
  return _AV[seed % _AV.length];
}

// ── Sidebar menu definition ─────────────────────────────────────────────────
const CFG_MENU = [
  { id: "perfil",    label: "Perfil",             icon: "user"     },
  { id: "assinatura",label: "Assinatura",          icon: "edit"     },
  { id: "equipe",    label: "Equipe & permissão",  icon: "people"   },
  { id: "whatsapp",  label: "WhatsApp & Sessões",  icon: "whatsapp" },
  { id: "crm",       label: "CRM & Funil",         icon: "deal"     },
  { id: "tags",      label: "Tags & categorias",   icon: "tag"      },
  { id: "horarios",  label: "Horários & SLA",      icon: "clock"    },
  { id: "safeIA",    label: "SAFE IA",             icon: "ai"       },
  { id: "webhooks",  label: "WebHooks & API",      icon: "link"     },
  { id: "plano",     label: "Plano & Faturamento", icon: "money"    },
  { id: "sistema",   label: "Sistema & Aparência", icon: "settings" },
  { id: "importar",  label: "Importar Conversas",  icon: "download" },
];

// Backward-compat: old tab keys → new keys
const TAB_ALIAS = {
  empresa: "perfil",
  equipe:  "equipe",
  whatsapp:"whatsapp",
  crm:     "crm",
  sistema: "sistema",
};

// ── Helper components ────────────────────────────────────────────────────────
function Info({ label, value }) {
  return <div className="infoItem"><span>{label}</span><strong>{value}</strong></div>;
}

function PlaceholderSection({ icon, title, desc = "Esta seção está sendo desenvolvida e estará disponível em breve." }) {
  return (
    <div className="cfgSection">
      <div className="cfgPlaceholder">
        <div className="cfgPlaceholderIcon"><Icon name={icon} size={28} /></div>
        <h3>{title}</h3>
        <p>{desc}</p>
        <span className="cfgPlaceholderBadge">Em desenvolvimento</span>
      </div>
    </div>
  );
}

// ── WhatsApp QR Panel ────────────────────────────────────────────────────────
function WhatsappQRPanel({ api, isAdmin }) {
  const [session, setSession] = useState({ status: "disconnected", qr: null, phone: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pairPhone, setPairPhone] = useState("");
  const intervalRef = React.useRef(null);

  async function loadStatus() {
    try {
      const response = await api.get("/whatsapp/session");
      setSession(response.data);
    } catch (err) {
      // Don't surface polling errors as banners — just log quietly
    }
  }

  // Adaptive polling: 3s when inactive/reconnecting, 8s when already connected
  // Initial load on mount is immediate (loadStatus called directly)
  useEffect(() => {
    loadStatus(); // immediate first check
    function schedule(st) {
      clearInterval(intervalRef.current);
      const delay = st === "connected" ? 8000 : 3000;
      intervalRef.current = setInterval(loadStatus, delay);
    }
    schedule(session.status);
    return () => clearInterval(intervalRef.current);
  }, [session.status]);  // eslint-disable-line

  async function connect() {
    setBusy(true); setError("");
    try { await api.post("/whatsapp/session/connect"); await loadStatus(); }
    catch (err) { setError(err.response?.data?.error || "Erro ao conectar."); }
    finally { setBusy(false); }
  }

  async function forceReconnect() {
    setBusy(true); setError("");
    try {
      // Disconnect first to clear the stuck state, then reconnect
      try { await api.post("/whatsapp/session/disconnect"); } catch {}
      await new Promise(r => setTimeout(r, 800));
      await api.post("/whatsapp/session/connect");
      await loadStatus();
    } catch (err) { setError(err.response?.data?.error || "Erro ao reconectar."); }
    finally { setBusy(false); }
  }

  async function clearAndReconnect() {
    setBusy(true); setError("");
    try {
      // Wipe stale credentials from disk then start fresh
      await api.post("/whatsapp/session/clear");
      await new Promise(r => setTimeout(r, 600));
      await api.post("/whatsapp/session/connect");
      await loadStatus();
    } catch (err) { setError(err.response?.data?.error || "Erro ao limpar sessão."); }
    finally { setBusy(false); }
  }

  async function disconnect() {
    setBusy(true); setError("");
    try { await api.post("/whatsapp/session/disconnect"); await loadStatus(); }
    catch (err) { setError(err.response?.data?.error || "Erro ao desconectar."); }
    finally { setBusy(false); }
  }

  async function requestPairCode() {
    setBusy(true); setError("");
    try { await api.post("/whatsapp/session/pair-code", { phone: pairPhone }); await loadStatus(); }
    catch (err) { setError(err.response?.data?.error || "Erro ao gerar código de pareamento."); }
    finally { setBusy(false); }
  }

  const statusLabel = {
    disconnected:         "Desconectado",
    connecting:           "Conectando…",
    qr_ready:             "Aguardando QR Code",
    pairing_code_ready:   "Código pronto",
    connected:            "Conectado",
    reconnecting:         "Reconectando…",
    managed_elsewhere:    "Gerenciado por outra instância",
  }[session.status] || session.status;

  const isConnected   = session.status === "connected";
  const isReconnecting = session.status === "reconnecting";
  const isConnecting  = session.status === "connecting";
  const isInactive    = session.status === "disconnected";

  const pillClass = isConnected ? "active"
    : (isReconnecting || isConnecting) ? "warning"
    : "idle";

  return (
    <div className="cfgCard cfgWhatsappCard">
      <div className="cfgCardHdr">
        <div>
          <h3>Conexão WhatsApp (QR Code)</h3>
          <p>Conecte escaneando o QR Code com o celular, igual ao WhatsApp Web.</p>
        </div>
        <span className={`cfgStatusPill ${pillClass}`}>{statusLabel}</span>
      </div>

      {error && <div className="cfgErrorBanner">{isAdmin ? error : "Falha na conexão WhatsApp — sistema tentando reconectar…"}</div>}

      {/* ── Connected ── */}
      {isConnected && (
        <div className="cfgInfoBox">
          <strong>Conectado como:</strong>
          <p style={{ fontSize: 15, marginTop: 4 }}>{session.phone || "—"}</p>
          {isAdmin && (
            <button className="btn btn-danger-ghost btn-sm" onClick={disconnect} disabled={busy} style={{ marginTop: 10 }}>
              Desconectar
            </button>
          )}
        </div>
      )}

      {/* ── QR Code — admin only ── */}
      {isAdmin && session.status === "qr_ready" && session.qr && (
        <div className="cfgQrWrap">
          <img src={session.qr} alt="QR Code" style={{ width: 240, height: 240, borderRadius: 8 }} />
          <p>Abra o WhatsApp → <strong>Aparelhos conectados</strong> → <strong>Conectar um aparelho</strong> → escaneie o QR Code acima.</p>
        </div>
      )}

      {/* ── Pairing code — admin only ── */}
      {isAdmin && session.status === "pairing_code_ready" && session.pairingCode && (
        <div className="cfgInfoBox">
          <strong>Código de pareamento</strong>
          <p style={{ fontSize: 28, letterSpacing: 3, textAlign: "center", marginTop: 8 }}>{session.pairingCode}</p>
          <p style={{ textAlign: "center", marginTop: 4 }}>Informe este código no WhatsApp do celular.</p>
        </div>
      )}

      {/* ── Reconnecting — auto-reconnect is running ── */}
      {isReconnecting && (
        <div className="cfgReconnectBox">
          <div className="cfgReconnectSpinner" />
          <div>
            <strong>Reconectando automaticamente…</strong>
            <p>O sistema está tentando restaurar a conexão. Aguarde alguns instantes.</p>
          </div>
          {isAdmin && (
            <button className="btn btn-sm btn-primary" onClick={forceReconnect} disabled={busy} style={{ marginLeft: "auto", flexShrink: 0 }}>
              {busy ? "Aguarde…" : "Forçar agora"}
            </button>
          )}
        </div>
      )}

      {/* ── Connecting (waiting for QR) ── */}
      {isConnecting && (
        <div className="cfgReconnectBox">
          <div className="cfgReconnectSpinner" />
          <p style={{ color: "var(--text-3)" }}>Iniciando conexão… aguarde o QR Code aparecer.</p>
        </div>
      )}

      {/* ── Disconnected ─────────────────────────────────────────────────────── */}
      {isInactive && (
        isAdmin ? (
          /* Admin: botão principal + opção de limpeza em caso de erro */
          <div style={{ padding: "16px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <button className="btn btn-primary" onClick={connect} disabled={busy}>
              {busy ? "Iniciando…" : "Iniciar conexão"}
            </button>
            {error && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={clearAndReconnect}
                disabled={busy}
                title="Apaga as credenciais salvas no servidor e inicia uma nova conexão com QR fresco"
              >
                {busy ? "Limpando…" : "Limpar sessão e reconectar"}
              </button>
            )}
          </div>
        ) : (
          /* Operador: pode tentar reconectar (restaura creds salvas sem exibir QR) */
          <div style={{ padding: "12px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <p style={{ fontSize: 13, color: "var(--text-3)", textAlign: "center", margin: 0 }}>
              WhatsApp desconectado. O sistema reconecta automaticamente — ou tente agora:
            </p>
            <button className="btn btn-secondary btn-sm" onClick={connect} disabled={busy}>
              {busy ? "Reconectando…" : "Tentar reconectar"}
            </button>
            {error && (
              <p style={{ fontSize: 12, color: "var(--danger, #ff4d4f)", textAlign: "center", margin: 0 }}>
                {error} — Se persistir, solicite ao administrador.
              </p>
            )}
          </div>
        )
      )}

      {/* ── Pairing by code — admin only ── */}
      {isAdmin && !isConnected && (
        <div className="cfgInfoBox" style={{ marginTop: 12 }}>
          <strong>Parear por código</strong>
          <p>Se o QR continuar instável, gere um código numérico para vincular pelo telefone.</p>
          <div className="cfgInlineRow" style={{ marginTop: 10 }}>
            <input value={pairPhone} onChange={(e) => setPairPhone(e.target.value)} placeholder="Ex: 5583991126098" style={{ flex: 1 }} />
            <button className="btn btn-primary" type="button" onClick={requestPairCode} disabled={busy || !pairPhone.trim()}>
              {busy ? "Gerando…" : "Gerar código"}
            </button>
          </div>
        </div>
      )}

      <div className="cfgWarningBox" style={{ marginTop: 12 }}>
        <Icon name="warning" size={14} />
        <p>Esta conexão usa o protocolo do WhatsApp Web e não é uma integração oficial da Meta. Use com responsabilidade — evite disparos em massa.</p>
      </div>
    </div>
  );
}

// ── Settings Page ────────────────────────────────────────────────────────────
function SettingsPage({ api, user, initialTab, clearInitialTab, onUserUpdate }) {
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmDeleteNumber, setConfirmDeleteNumber] = useState(null);
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(null);
  const [confirmDeleteQuickReply, setConfirmDeleteQuickReply] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const t = initialTab || "perfil";
    return TAB_ALIAS[t] || t;
  });

  useEffect(() => {
    if (initialTab) {
      setActiveTab(TAB_ALIAS[initialTab] || initialTab);
      clearInitialTab?.();
    }
  }, [initialTab]); // eslint-disable-line

  // Funnel stages
  const [funnelStages, setFunnelStages] = useState([]);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editingStageName, setEditingStageName] = useState("");
  const [newStageName, setNewStageName] = useState("");
  const [funnelNotice, setFunnelNotice] = useState("");

  // Signature
  const [sig, setSig] = useState({ enabled: false, template: signatureTemplatePresets[0].template });
  const [sigPreset, setSigPreset] = useState("brackets");
  const [sigSaving, setSigSaving] = useState(false);
  const sigTextareaRef = useRef(null);

  function insertSigFormat(wrap) {
    const el = sigTextareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = el.value.slice(start, end) || "texto";
    const [open, close] = Array.isArray(wrap) ? wrap : [wrap, wrap];
    const inserted = open + selected + close;
    const next = el.value.slice(0, start) + inserted + el.value.slice(end);
    setSigPreset("custom");
    setSig((s) => ({ ...s, template: next }));
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + open.length, start + open.length + selected.length);
    }, 0);
  }
  const [sigSaved, setSigSaved] = useState(false);
  const [sigError, setSigError] = useState("");

  // Profile form (personal data of logged-in user)
  const [profileForm, setProfileForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // User invite form
  const [form, setForm] = useState({ name: "", email: "", password: "123456", role: "atendente" });

  // Company form
  const [companyForm, setCompanyForm] = useState({ name: "", cnpj: "", phone: "", email: "", address: "", enter_to_send: true });

  // Number form
  const [numberForm, setNumberForm] = useState({ id: null, label: "", number: "", status: "aguardando_integracao", provider: "cloud_api", phone_number_id: "", business_account_id: "" });

  // Template form
  const [templateForm, setTemplateForm] = useState({ id: null, name: "", language_code: "pt_BR", category: "utility", status: "approved", body_text: "", example_values: "" });

  // Quick reply form
  const [quickReplyForm, setQuickReplyForm] = useState({ id: null, title: "", shortcut: "", category: "atendimento", body_text: "", is_favorite: true });

  // ── Data loaders ──────────────────────────────────────────────────────────
  async function loadUsers() {
    const response = await api.get("/users");
    setUsers(response.data);
  }

  async function loadCompany() {
    const response = await api.get("/company");
    setCompany(response.data);
    setCompanyForm({
      name: response.data?.name || "",
      cnpj: response.data?.cnpj || "",
      phone: response.data?.phone || "",
      email: response.data?.email || "",
      address: response.data?.address || "",
      enter_to_send: Number(response.data?.enter_to_send ?? 1) === 1,
    });
    localStorage.setItem("safezap_enter_to_send", Number(response.data?.enter_to_send ?? 1) === 1 ? "1" : "0");
  }

  async function loadWhatsappNumbers() {
    const response = await api.get("/whatsapp-numbers");
    setWhatsappNumbers(response.data);
  }

  async function loadWhatsappTemplates() {
    const response = await api.get("/whatsapp-templates");
    setWhatsappTemplates(response.data || []);
  }

  async function loadQuickReplies() {
    const response = await api.get("/quick-replies");
    setQuickReplies(response.data || []);
  }

  async function loadFunnelStages() {
    try { const res = await api.get("/funnel/stages"); setFunnelStages(res.data || []); }
    catch {}
  }

  async function loadSignature() {
    try {
      const res = await api.get("/me/signature");
      const tmpl = normalizeSignatureTemplate(res.data?.signature_template || signatureTemplatePresets[0].template);
      const matched = signatureTemplatePresets.find((p) => p.template && p.template === tmpl);
      setSig({ enabled: Number(res.data?.signature_enabled || 0) === 1, template: tmpl });
      setSigPreset(matched?.id || "custom");
    } catch { /* silent */ }
  }

  async function loadAll() {
    setLoading(true);
    try { await Promise.all([loadUsers(), loadCompany(), loadWhatsappNumbers(), loadWhatsappTemplates(), loadQuickReplies(), loadFunnelStages(), loadSignature()]); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  // ── Funnel ────────────────────────────────────────────────────────────────
  async function renameFunnelStage(id, name) {
    try {
      await api.patch(`/funnel/stages/${id}`, { name });
      setEditingStageId(null); setEditingStageName("");
      setFunnelNotice("Etapa renomeada.");
      await loadFunnelStages();
    } catch (err) { setFunnelNotice(err.response?.data?.error || "Erro ao renomear etapa."); }
  }

  async function deleteFunnelStage(id) {
    if (!window.confirm("Excluir esta etapa? Leads nela precisarão ser movidos primeiro.")) return;
    try { await api.delete(`/funnel/stages/${id}`); setFunnelNotice("Etapa excluída."); await loadFunnelStages(); }
    catch (err) { setFunnelNotice(err.response?.data?.error || "Erro ao excluir etapa."); }
  }

  async function createFunnelStage(e) {
    e.preventDefault();
    if (!newStageName.trim()) return;
    try { await api.post("/funnel/stages", { name: newStageName.trim() }); setNewStageName(""); setFunnelNotice("Etapa criada."); await loadFunnelStages(); }
    catch (err) { setFunnelNotice(err.response?.data?.error || "Erro ao criar etapa."); }
  }

  async function moveFunnelStage(stage, direction) {
    const idx = funnelStages.findIndex((s) => s.id === stage.id);
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === funnelStages.length - 1) return;
    const newOrder = [...funnelStages];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    try { await api.post("/funnel/stages/reorder", { order: newOrder.map((s) => s.id) }); await loadFunnelStages(); }
    catch {}
  }

  // ── Profile ───────────────────────────────────────────────────────────────
  async function saveProfile(e) {
    e.preventDefault();
    if (!profileForm.name.trim()) { setNotice("Nome é obrigatório."); return; }
    setProfileSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { name: profileForm.name, phone: profileForm.phone });
      onUserUpdate?.({ name: profileForm.name, phone: profileForm.phone });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err) {
      setNotice(err._friendlyMessage || err.response?.data?.error || "Erro ao salvar perfil. Tente novamente.");
    } finally { setProfileSaving(false); }
  }

  // ── Company ───────────────────────────────────────────────────────────────
  async function saveCompany(e) {
    e.preventDefault();
    if (!companyForm.name.trim()) { setNotice("Nome da empresa é obrigatório."); return; }
    try {
      const response = await api.put("/company", companyForm);
      setCompany(response.data);
      localStorage.setItem("safezap_enter_to_send", companyForm.enter_to_send ? "1" : "0");
      setNotice("Dados da empresa salvos.");
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao salvar empresa."); }
  }

  // ── Signature (per-user) ──────────────────────────────────────────────────
  async function saveSignatureToggle(enabled) {
    setSig((s) => ({ ...s, enabled }));
    setSigSaving(true); setSigError(""); setSigSaved(false);
    try {
      await api.patch("/me/signature", { enabled, template: sig.template });
      setSigSaved(true);
      setTimeout(() => setSigSaved(false), 2500);
    } catch (err) {
      setSig((s) => ({ ...s, enabled: !enabled }));
      setSigError(err.response?.data?.error || "Erro ao salvar assinatura.");
    } finally { setSigSaving(false); }
  }

  async function saveSignatureTemplate(e) {
    e.preventDefault();
    setSigSaving(true); setSigError(""); setSigSaved(false);
    try {
      await api.patch("/me/signature", { enabled: sig.enabled, template: sig.template });
      setSigSaved(true);
      setTimeout(() => setSigSaved(false), 2500);
    } catch (err) { setSigError(err.response?.data?.error || "Erro ao salvar template."); }
    finally { setSigSaving(false); }
  }

  // ── Users ─────────────────────────────────────────────────────────────────
  function updateField(field, value) { setForm((old) => ({ ...old, [field]: value })); }

  async function createUser(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) { setNotice("Preencha nome, email e senha."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setNotice("E-mail inválido."); return; }
    if (form.password.length < 6) { setNotice("A senha deve ter pelo menos 6 caracteres."); return; }
    try {
      await api.post("/users", form);
      setNotice("Atendente criado com sucesso.");
      setForm({ name: "", email: "", password: "123456", role: "atendente" });
      await loadUsers();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao criar atendente."); }
  }

  async function toggleUserStatus(item) {
    if (item.id === user.id) { setNotice("Você não pode desativar o próprio usuário logado."); return; }
    try {
      await api.patch(`/users/${item.id}/status`, { active: item.active !== 1 });
      setNotice(item.active === 1 ? "Atendente desativado." : "Atendente ativado.");
      await loadUsers();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao alterar status."); }
  }

  // ── WhatsApp numbers ──────────────────────────────────────────────────────
  function resetNumberForm() { setNumberForm({ id: null, label: "", number: "", status: "aguardando_integracao", provider: "cloud_api", phone_number_id: "", business_account_id: "" }); }
  function editWhatsappNumber(item) { setNumberForm({ id: item.id, label: item.label || "", number: item.number || "", status: item.status || "aguardando_integracao", provider: item.provider || "cloud_api", phone_number_id: item.phone_number_id || "", business_account_id: item.business_account_id || "" }); }

  async function saveWhatsappNumber(e) {
    e.preventDefault();
    if (!numberForm.label.trim() || !numberForm.number.trim()) { setNotice("Informe o nome do setor e o número."); return; }
    try {
      if (numberForm.id) { await api.put(`/whatsapp-numbers/${numberForm.id}`, numberForm); setNotice("Número atualizado."); }
      else { await api.post("/whatsapp-numbers", numberForm); setNotice("Número cadastrado."); }
      resetNumberForm(); await loadWhatsappNumbers();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao salvar número."); }
  }

  async function deleteWhatsappNumber(item) {
    try { await api.delete(`/whatsapp-numbers/${item.id}`); setConfirmDeleteNumber(null); setNotice("Número excluído."); await loadWhatsappNumbers(); }
    catch (err) { setConfirmDeleteNumber(null); setNotice(err.response?.data?.error || "Erro ao excluir número."); }
  }

  // ── WhatsApp templates ────────────────────────────────────────────────────
  function resetTemplateForm() { setTemplateForm({ id: null, name: "", language_code: "pt_BR", category: "utility", status: "approved", body_text: "", example_values: "" }); }
  function editWhatsappTemplate(item) { setTemplateForm({ id: item.id, name: item.name || "", language_code: item.language_code || "pt_BR", category: item.category || "utility", status: item.status || "approved", body_text: item.body_text || "", example_values: formatTemplateVariables(item.example_values || []) }); }

  async function saveWhatsappTemplate(e) {
    e.preventDefault();
    if (!templateForm.name.trim() || !templateForm.body_text.trim()) { setNotice("Informe o nome e o conteúdo do template."); return; }
    try {
      const payload = { ...templateForm, example_values: parseTemplateVariables(templateForm.example_values) };
      if (templateForm.id) { await api.put(`/whatsapp-templates/${templateForm.id}`, payload); setNotice("Template atualizado."); }
      else { await api.post("/whatsapp-templates", payload); setNotice("Template cadastrado."); }
      resetTemplateForm(); await loadWhatsappTemplates();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao salvar template."); }
  }

  async function deleteWhatsappTemplate(item) {
    try { await api.delete(`/whatsapp-templates/${item.id}`); setConfirmDeleteTemplate(null); setNotice("Template excluído."); await loadWhatsappTemplates(); }
    catch (err) { setConfirmDeleteTemplate(null); setNotice(err.response?.data?.error || "Erro ao excluir template."); }
  }

  // ── Quick replies ─────────────────────────────────────────────────────────
  function resetQuickReplyForm() { setQuickReplyForm({ id: null, title: "", shortcut: "", category: "atendimento", body_text: "", is_favorite: true }); }
  function editQuickReply(item) { setQuickReplyForm({ id: item.id, title: item.title || "", shortcut: item.shortcut || "", category: item.category || "atendimento", body_text: item.body_text || "", is_favorite: Boolean(item.is_favorite) }); }

  async function saveQuickReply(e) {
    e.preventDefault();
    if (!quickReplyForm.title.trim() || !quickReplyForm.body_text.trim()) { setNotice("Informe o título e o conteúdo da resposta rápida."); return; }
    try {
      const payload = { ...quickReplyForm, shortcut: quickReplyForm.shortcut.trim() };
      if (quickReplyForm.id) { await api.put(`/quick-replies/${quickReplyForm.id}`, payload); setNotice("Resposta rápida atualizada."); }
      else { await api.post("/quick-replies", payload); setNotice("Resposta rápida cadastrada."); }
      resetQuickReplyForm(); await loadQuickReplies();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao salvar resposta rápida."); }
  }

  async function deleteQuickReply(item) {
    try { await api.delete(`/quick-replies/${item.id}`); setConfirmDeleteQuickReply(null); setNotice("Resposta rápida excluída."); await loadQuickReplies(); }
    catch (err) { setConfirmDeleteQuickReply(null); setNotice(err.response?.data?.error || "Erro ao excluir resposta rápida."); }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const is = (tab) => activeTab === tab;

  function numberStatusLabel(status) {
    return { aguardando_integracao: "Aguardando", conectado: "Conectado", inativo: "Inativo", erro: "Erro" }[status] || status;
  }

  const connectedNumbers      = whatsappNumbers.filter((n) => n.status === "conectado").length;
  const approvedTemplatesCount= whatsappTemplates.filter((t) => t.status === "approved").length;
  const favoriteQuickReplies  = quickReplies.filter((q) => q.is_favorite).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Modais de confirmação ── */}
      {confirmDeleteNumber && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h3>Confirmar exclusão</h3>
            <p>Excluir o número <strong>{confirmDeleteNumber.label}</strong> ({confirmDeleteNumber.number})?</p>
            <div className="modalActions">
              <button className="dangerBtn" onClick={() => deleteWhatsappNumber(confirmDeleteNumber)}>Excluir</button>
              <button className="secondaryBtn" onClick={() => setConfirmDeleteNumber(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteTemplate && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h3>Confirmar exclusão</h3>
            <p>Excluir o template <strong>{confirmDeleteTemplate.name}</strong>?</p>
            <div className="modalActions">
              <button className="dangerBtn" onClick={() => deleteWhatsappTemplate(confirmDeleteTemplate)}>Excluir</button>
              <button className="secondaryBtn" onClick={() => setConfirmDeleteTemplate(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteQuickReply && (
        <div className="modalOverlay">
          <div className="modalBox">
            <h3>Confirmar exclusão</h3>
            <p>Excluir a resposta rápida <strong>{confirmDeleteQuickReply.title}</strong>?</p>
            <div className="modalActions">
              <button className="dangerBtn" onClick={() => deleteQuickReply(confirmDeleteQuickReply)}>Excluir</button>
              <button className="secondaryBtn" onClick={() => setConfirmDeleteQuickReply(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div className="cfgNotice" onClick={() => setNotice("")}>{notice}</div>
      )}

      <div className="page-v2">
      <div className="page-header-v2">
        <div>
          <div className="page-title-v2">Configurações</div>
          <div className="page-subtitle-v2">Gerencie sua conta, equipe e integrações da operação</div>
        </div>
      </div>

      <div className="settingsLayout">
        {/* ── Sidebar de navegação interna ── */}
        <nav className="cfgSidebar">
          {CFG_MENU.map((item) => (
            <button
              key={item.id}
              className={`cfgSidebarItem ${is(item.id) ? "active" : ""}`}
              onClick={() => setActiveTab(item.id)}
            >
              <Icon name={item.icon} size={13} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* ── Conteúdo da seção ── */}
        <div className="cfgContent">

          {/* ════════════════════════════════════════════════════
              1. PERFIL
          ════════════════════════════════════════════════════ */}
          {is("perfil") && (
            <div className="cfgSection">
              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <div>
                    <h3>Perfil do usuário</h3>
                    <p>Suas informações de conta e acesso</p>
                  </div>
                </div>
                <div className="cfgProfileRow">
                  <div className="cfgAvatarCircle" style={(() => { const c = getAvatarColor(user?.name); return { background: c + "22", color: c, borderColor: c + "55" }; })()}>
                    {getInitials(user?.name || "U")}
                  </div>
                  <div className="cfgProfileMeta">
                    <strong>{user?.name}</strong>
                    <span>{user?.email}</span>
                    <em className="cfgRolePill">{user?.role === "admin" ? "Administrador" : "Atendente"}</em>
                  </div>
                </div>

                <form className="cfgForm" onSubmit={saveProfile} style={{ marginTop: 20 }}>
                  <div className="cfgFormGrid">
                    <div className="cfgFormField">
                      <label>Nome completo</label>
                      <input value={profileForm.name} onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))} placeholder="Seu nome" />
                    </div>
                    <div className="cfgFormField">
                      <label>E-mail</label>
                      <input value={user?.email || ""} disabled />
                    </div>
                    <div className="cfgFormField">
                      <label>Telefone</label>
                      <input value={profileForm.phone} onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))} placeholder="(83) 99999-9999" />
                    </div>
                    <div className="cfgFormField">
                      <label>Função</label>
                      <input value={user?.role === "admin" ? "Administrador" : "Atendente"} disabled />
                    </div>
                  </div>
                  <div className="cfgFormActions">
                    {profileSaved && <span className="cfgSavedBadge">✓ Salvo</span>}
                    <button type="submit" className="btn btn-primary" disabled={profileSaving}>
                      {profileSaving ? "Salvando…" : "Salvar perfil"}
                    </button>
                  </div>
                </form>
              </div>

              {user?.role === "admin" && (
                <div className="cfgCard">
                  <div className="cfgCardHdr">
                    <div>
                      <h3>Dados da empresa</h3>
                      <p>Informações da organização no sistema</p>
                    </div>
                  </div>
                  <form className="cfgForm" onSubmit={saveCompany}>
                    <div className="cfgFormGrid">
                      <div className="cfgFormField">
                        <label>Nome da empresa</label>
                        <input value={companyForm.name} onChange={(e) => setCompanyForm((old) => ({ ...old, name: e.target.value }))} />
                      </div>
                      <div className="cfgFormField">
                        <label>CNPJ</label>
                        <input value={companyForm.cnpj} onChange={(e) => setCompanyForm((old) => ({ ...old, cnpj: e.target.value }))} placeholder="00.000.000/0000-00" />
                      </div>
                      <div className="cfgFormField">
                        <label>Telefone</label>
                        <input value={companyForm.phone} onChange={(e) => setCompanyForm((old) => ({ ...old, phone: e.target.value }))} placeholder="(83) 99999-9999" />
                      </div>
                      <div className="cfgFormField">
                        <label>E-mail</label>
                        <input value={companyForm.email} onChange={(e) => setCompanyForm((old) => ({ ...old, email: e.target.value }))} placeholder="empresa@email.com" />
                      </div>
                    </div>
                    <div className="cfgFormField">
                      <label>Endereço</label>
                      <input value={companyForm.address} onChange={(e) => setCompanyForm((old) => ({ ...old, address: e.target.value }))} placeholder="Rua, número, bairro, cidade" />
                    </div>
                    <div className="cfgSwitchRow">
                      <div>
                        <strong>Enter para enviar mensagem</strong>
                        <p>Pressionar Enter envia. Use Shift+Enter para quebrar linha.</p>
                      </div>
                      <label className="switch">
                        <input type="checkbox" checked={companyForm.enter_to_send} onChange={(e) => setCompanyForm((old) => ({ ...old, enter_to_send: e.target.checked }))} />
                        <span />
                      </label>
                    </div>
                    <div className="cfgFormActions">
                      <button type="submit" className="btn btn-primary">Salvar empresa</button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              2. ASSINATURA DA MENSAGEM
          ════════════════════════════════════════════════════ */}
          {is("assinatura") && (
            <div className="cfgSection">
              {sigError && <div className="cfgErrorBanner">{sigError}</div>}

              <div className="cfgCard">
                <div className="cfgSwitchRow">
                  <div>
                    <h3>Minha assinatura pessoal</h3>
                    <p>Sua assinatura é adicionada automaticamente nas suas mensagens. Cada usuário tem a própria.</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {sigSaved && <span className="cfgSavedBadge">✓ Salvo</span>}
                    {sigSaving && !sigSaved && <span className="cfgSavingBadge">Salvando…</span>}
                    <span className={`cfgStatusPill ${sig.enabled ? "active" : "idle"}`}>{sig.enabled ? "Ativa" : "Inativa"}</span>
                    <label className="switch">
                      <input type="checkbox" checked={sig.enabled} disabled={sigSaving} onChange={(e) => saveSignatureToggle(e.target.checked)} />
                      <span />
                    </label>
                  </div>
                </div>
              </div>

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Personalização do template</h3>
                </div>
                <form className="cfgForm" onSubmit={saveSignatureTemplate}>
                  <div className="cfgFormField">
                    <label>Modelo pré-definido</label>
                    <div className="cfgChipRow">
                      {signatureTemplatePresets.map((x) => (
                        <button
                          key={x.id}
                          type="button"
                          className={`cfgChip ${sigPreset === x.id ? "active" : ""}`}
                          onClick={() => {
                            setSigPreset(x.id);
                            if (x.id !== "custom") {
                              const found = signatureTemplatePresets.find((p) => p.id === x.id);
                              setSig((s) => ({ ...s, template: found?.template || s.template }));
                            }
                          }}
                        >
                          {x.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="cfgFormField">
                    <label>Template enviado</label>
                    <div className="sigFormatBar">
                      <button type="button" className="sigFmtBtn" title="Negrito (*texto*)" onClick={() => insertSigFormat("*")}><strong>B</strong></button>
                      <button type="button" className="sigFmtBtn" title="Itálico (_texto_)" onClick={() => insertSigFormat("_")}><em>I</em></button>
                      <button type="button" className="sigFmtBtn" title="Código inline (```texto```)" onClick={() => insertSigFormat(["```", "```"])}><code style={{ fontSize: 11 }}>{"<>"}</code></button>
                    </div>
                    <textarea
                      ref={sigTextareaRef}
                      value={sig.template}
                      rows={3}
                      onChange={(e) => { setSigPreset("custom"); setSig((s) => ({ ...s, template: e.target.value })); }}
                      placeholder="Use {agent} e {message}"
                    />
                    <small className="cfgHint">Variáveis: <code>{"{agent}"}</code> = nome do atendente · <code>{"{message}"}</code> = texto da mensagem</small>
                  </div>

                  <div className="cfgPreviewBox">
                    <span className="cfgPreviewLabel">Prévia da mensagem</span>
                    <pre>{formatSignaturePreview(sig.template, user?.name || "Atendente", "Oi, tudo bem por aí?")}</pre>
                  </div>

                  <div className="cfgFormActions">
                    {sigSaved && <span className="cfgSavedBadge">✓ Salvo</span>}
                    <button type="submit" className="btn btn-primary" disabled={sigSaving}>
                      {sigSaving ? "Salvando…" : "Salvar template"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              3. EQUIPE & PERMISSÃO
          ════════════════════════════════════════════════════ */}
          {is("equipe") && (
            <div className="cfgSection">
              <div className="cfgKpiRow">
                {[
                  { label: "Total",    value: users.length },
                  { label: "Ativos",   value: users.filter((u) => u.active === 1).length },
                  { label: "Admins",   value: users.filter((u) => u.role === "admin").length },
                  { label: "Inativos", value: users.filter((u) => u.active !== 1).length },
                ].map((k) => (
                  <div key={k.label} className="cfgKpi">
                    <strong>{k.value}</strong>
                    <span>{k.label}</span>
                  </div>
                ))}
              </div>

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Membros da equipe</h3>
                  <span className="cfgCardBadge">{users.length} usuário(s)</span>
                </div>
                {loading ? (
                  <p className="cfgLoadingText">Carregando equipe…</p>
                ) : (
                  <div className="cfgMemberList">
                    {users.map((item) => (
                      <div className="cfgMemberRow" key={item.id}>
                        <div className="cfgMemberAvatar">
                          {item.name?.slice(0, 1)?.toUpperCase()}
                        </div>
                        <div className="cfgMemberInfo">
                          <strong>{item.name}</strong>
                          <span>{item.email}</span>
                          <small>
                            <em className={`cfgRolePill ${item.role === "admin" ? "admin" : ""}`}>
                              {item.role === "admin" ? "Admin" : "Atendente"}
                            </em>
                            {" · "}
                            <span className={item.active === 1 ? "cfgActiveText" : "cfgInactiveText"}>
                              {item.active === 1 ? "ativo" : "inativo"}
                            </span>
                          </small>
                        </div>
                        <button
                          className={item.active === 1 ? "btn btn-danger-ghost btn-sm" : "btn btn-secondary btn-sm"}
                          onClick={() => toggleUserStatus(item)}
                        >
                          {item.active === 1 ? "Desativar" : "Ativar"}
                        </button>
                      </div>
                    ))}
                    {!users.length && <p className="cfgEmptyText">Nenhum usuário cadastrado.</p>}
                  </div>
                )}
              </div>

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Convidar membro</h3>
                </div>
                <form className="cfgForm" onSubmit={createUser}>
                  <div className="cfgFormGrid">
                    <div className="cfgFormField">
                      <label>Nome</label>
                      <input value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Ex: Maria Atendimento" />
                    </div>
                    <div className="cfgFormField">
                      <label>E-mail de login</label>
                      <input value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder="maria@empresa.com" />
                    </div>
                    <div className="cfgFormField">
                      <label>Senha inicial</label>
                      <input value={form.password} onChange={(e) => updateField("password", e.target.value)} placeholder="Mínimo 6 caracteres" />
                    </div>
                    <div className="cfgFormField">
                      <label>Permissão</label>
                      <select value={form.role} onChange={(e) => updateField("role", e.target.value)}>
                        <option value="atendente">Atendente</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>
                  </div>
                  <div className="cfgFormActions">
                    <button type="submit" className="btn btn-primary">Cadastrar membro</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              4. WHATSAPP & SESSÕES
          ════════════════════════════════════════════════════ */}
          {is("whatsapp") && (
            <div className="cfgSection">
              <WhatsappQRPanel api={api} isAdmin={user?.role === "admin" || user?.role === "master"} />

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Números e setores</h3>
                  <span className="cfgCardBadge">{whatsappNumbers.length} número(s)</span>
                </div>

                <form className="cfgForm" onSubmit={saveWhatsappNumber}>
                  <div className="cfgFormGrid">
                    <div className="cfgFormField">
                      <label>Setor / etiqueta</label>
                      <input value={numberForm.label} onChange={(e) => setNumberForm((old) => ({ ...old, label: e.target.value }))} placeholder="Ex: Comercial" />
                    </div>
                    <div className="cfgFormField">
                      <label>Número</label>
                      <input value={numberForm.number} onChange={(e) => setNumberForm((old) => ({ ...old, number: maskBrazilPhoneInput(e.target.value) }))} placeholder="+55 (83) 99999-9999" />
                    </div>
                    <div className="cfgFormField">
                      <label>Status</label>
                      <select value={numberForm.status} onChange={(e) => setNumberForm((old) => ({ ...old, status: e.target.value }))}>
                        <option value="aguardando_integracao">Aguardando integração</option>
                        <option value="conectado">Conectado</option>
                        <option value="inativo">Inativo</option>
                        <option value="erro">Erro</option>
                      </select>
                    </div>
                    <div className="cfgFormField">
                      <label>Provedor</label>
                      <select value={numberForm.provider} onChange={(e) => setNumberForm((old) => ({ ...old, provider: e.target.value }))}>
                        <option value="cloud_api">Meta Cloud API</option>
                        <option value="baileys">WhatsApp QR</option>
                      </select>
                    </div>
                    {numberForm.provider === "cloud_api" && (
                      <>
                        <div className="cfgFormField">
                          <label>Phone number ID</label>
                          <input value={numberForm.phone_number_id} onChange={(e) => setNumberForm((old) => ({ ...old, phone_number_id: e.target.value }))} placeholder="ID oficial da Meta" />
                        </div>
                        <div className="cfgFormField">
                          <label>Business account ID</label>
                          <input value={numberForm.business_account_id} onChange={(e) => setNumberForm((old) => ({ ...old, business_account_id: e.target.value }))} placeholder="Conta comercial Meta" />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="cfgFormActions">
                    {numberForm.id && <button type="button" className="btn btn-secondary" onClick={resetNumberForm}>Cancelar edição</button>}
                    <button type="submit" className="btn btn-primary">{numberForm.id ? "Salvar número" : "Cadastrar número"}</button>
                  </div>
                </form>

                <div className="cfgNumberList">
                  {whatsappNumbers.map((item) => (
                    <div className="cfgNumberCard" key={item.id}>
                      <div className="cfgNumberHead">
                        <div className="cfgNumberInfo">
                          <strong>{item.label}</strong>
                          <span>{formatBrazilPhone(item.number || "") || item.number}</span>
                        </div>
                        <span className={`cfgStatusPill ${item.status === "conectado" ? "active" : item.status === "erro" ? "danger" : "idle"}`}>
                          {numberStatusLabel(item.status)}
                        </span>
                      </div>
                      <div className="cfgNumberMeta">
                        <Info label="Canal" value={item.provider === "baileys" ? "WhatsApp QR" : "Meta Cloud API"} />
                        <Info label="Phone ID" value={item.phone_number_id || "Não definido"} />
                      </div>
                      <div className="cfgNumberActions">
                        <button className="btn btn-secondary" onClick={() => editWhatsappNumber(item)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteNumber(item)}>Excluir</button>
                      </div>
                    </div>
                  ))}
                  {!whatsappNumbers.length && <p className="cfgEmptyText">Nenhum número cadastrado.</p>}
                </div>
              </div>

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Templates operacionais</h3>
                  <span className="cfgCardBadge">{whatsappTemplates.length} template(s)</span>
                </div>

                <form className="cfgForm" onSubmit={saveWhatsappTemplate}>
                  <div className="cfgFormGrid">
                    <div className="cfgFormField">
                      <label>Nome interno / Meta</label>
                      <input value={templateForm.name} onChange={(e) => setTemplateForm((old) => ({ ...old, name: e.target.value }))} placeholder="followup_pagamento" />
                    </div>
                    <div className="cfgFormField">
                      <label>Idioma</label>
                      <input value={templateForm.language_code} onChange={(e) => setTemplateForm((old) => ({ ...old, language_code: e.target.value }))} placeholder="pt_BR" />
                    </div>
                    <div className="cfgFormField">
                      <label>Categoria</label>
                      <select value={templateForm.category} onChange={(e) => setTemplateForm((old) => ({ ...old, category: e.target.value }))}>
                        <option value="utility">Utilidade</option>
                        <option value="marketing">Marketing</option>
                        <option value="authentication">Autenticação</option>
                      </select>
                    </div>
                    <div className="cfgFormField">
                      <label>Status</label>
                      <select value={templateForm.status} onChange={(e) => setTemplateForm((old) => ({ ...old, status: e.target.value }))}>
                        <option value="approved">Aprovado</option>
                        <option value="pending">Pendente</option>
                        <option value="draft">Rascunho</option>
                        <option value="rejected">Rejeitado</option>
                      </select>
                    </div>
                  </div>
                  <div className="cfgFormField">
                    <label>Texto do template</label>
                    <textarea value={templateForm.body_text} onChange={(e) => setTemplateForm((old) => ({ ...old, body_text: e.target.value }))} placeholder="Olá {{1}}, passando para confirmar o pedido {{2}}." />
                  </div>
                  <div className="cfgFormField">
                    <label>Valores de exemplo</label>
                    <input value={templateForm.example_values} onChange={(e) => setTemplateForm((old) => ({ ...old, example_values: e.target.value }))} placeholder="Cliente Exemplo, PED-1024" />
                    <small className="cfgHint">Separe exemplos por vírgula.</small>
                  </div>
                  <div className="cfgPreviewBox">
                    <span className="cfgPreviewLabel">Prévia do template</span>
                    <pre>{renderTemplatePreview(templateForm.body_text, parseTemplateVariables(templateForm.example_values)) || "A prévia aparece aqui."}</pre>
                  </div>
                  <div className="cfgFormActions">
                    {templateForm.id && <button type="button" className="btn btn-secondary" onClick={resetTemplateForm}>Cancelar edição</button>}
                    <button type="submit" className="btn btn-primary">{templateForm.id ? "Salvar template" : "Cadastrar template"}</button>
                  </div>
                </form>

                <div className="cfgNumberList" style={{ marginTop: 8 }}>
                  {whatsappTemplates.map((item) => (
                    <div className="cfgNumberCard" key={item.id}>
                      <div className="cfgNumberHead">
                        <div className="cfgNumberInfo">
                          <strong>{item.name}</strong>
                          <span>{item.language_code} · {item.category}</span>
                        </div>
                        <span className={`cfgStatusPill ${item.status === "approved" ? "active" : item.status === "rejected" ? "danger" : "idle"}`}>
                          {item.status === "approved" ? "Aprovado" : item.status === "pending" ? "Pendente" : item.status === "rejected" ? "Rejeitado" : "Rascunho"}
                        </span>
                      </div>
                      <p className="cfgTemplatePreviewText">{renderTemplatePreview(item.body_text, item.example_values || []) || item.body_text}</p>
                      <div className="cfgNumberActions">
                        <button className="btn btn-secondary" onClick={() => editWhatsappTemplate(item)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteTemplate(item)}>Excluir</button>
                      </div>
                    </div>
                  ))}
                  {!whatsappTemplates.length && <p className="cfgEmptyText">Nenhum template cadastrado.</p>}
                </div>
              </div>

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Respostas rápidas</h3>
                  <span className="cfgCardBadge">{quickReplies.length} snippet(s)</span>
                </div>

                <form className="cfgForm" onSubmit={saveQuickReply}>
                  <div className="cfgFormGrid">
                    <div className="cfgFormField">
                      <label>Título</label>
                      <input value={quickReplyForm.title} onChange={(e) => setQuickReplyForm((old) => ({ ...old, title: e.target.value }))} placeholder="Ex: Follow-up proposta" />
                    </div>
                    <div className="cfgFormField">
                      <label>Atalho</label>
                      <input value={quickReplyForm.shortcut} onChange={(e) => setQuickReplyForm((old) => ({ ...old, shortcut: e.target.value }))} placeholder="proposta" />
                    </div>
                    <div className="cfgFormField">
                      <label>Categoria</label>
                      <select value={quickReplyForm.category} onChange={(e) => setQuickReplyForm((old) => ({ ...old, category: e.target.value }))}>
                        <option value="atendimento">Atendimento</option>
                        <option value="cobranca">Cobrança</option>
                        <option value="proposta">Proposta</option>
                        <option value="suporte">Suporte</option>
                        <option value="pos-venda">Pós-venda</option>
                      </select>
                    </div>
                    <div className="cfgFormField cfgFormFieldFull">
                      <label>Texto</label>
                      <textarea value={quickReplyForm.body_text} onChange={(e) => setQuickReplyForm((old) => ({ ...old, body_text: e.target.value }))} placeholder={"Olá {nome}, tudo bem?\n\nAqui é {atendente}."} />
                      <small className="cfgHint">Variáveis: <code>{"{nome}"}</code>, <code>{"{empresa}"}</code>, <code>{"{telefone}"}</code>, <code>{"{atendente}"}</code></small>
                    </div>
                  </div>
                  <label className="cfgCheckRow">
                    <input type="checkbox" checked={quickReplyForm.is_favorite} onChange={(e) => setQuickReplyForm((old) => ({ ...old, is_favorite: e.target.checked }))} />
                    <span>Favoritar para aparecer primeiro no chat</span>
                  </label>
                  <div className="cfgPreviewBox">
                    <span className="cfgPreviewLabel">Prévia do snippet</span>
                    <pre>{renderQuickReplyBody(quickReplyForm.body_text, { name: "Cliente Exemplo", company: companyForm.name || "Empresa Exemplo", phone: "+55 (83) 99999-9999", agent: user.name }) || "A prévia aparece aqui."}</pre>
                  </div>
                  <div className="cfgFormActions">
                    {quickReplyForm.id && <button type="button" className="btn btn-secondary" onClick={resetQuickReplyForm}>Cancelar edição</button>}
                    <button type="submit" className="btn btn-primary">{quickReplyForm.id ? "Salvar resposta rápida" : "Cadastrar resposta rápida"}</button>
                  </div>
                </form>

                <div className="cfgNumberList" style={{ marginTop: 8 }}>
                  {quickReplies.map((item) => (
                    <div className="cfgNumberCard" key={item.id}>
                      <div className="cfgNumberHead">
                        <div className="cfgNumberInfo">
                          <strong>{item.title}</strong>
                          <span>{item.shortcut ? `/${item.shortcut}` : "Sem atalho"} · {item.category}</span>
                        </div>
                        <span className={`cfgStatusPill ${item.is_favorite ? "active" : "idle"}`}>{item.is_favorite ? "Favorita" : "Padrão"}</span>
                      </div>
                      <p className="cfgTemplatePreviewText">{item.body_text}</p>
                      <div className="cfgNumberActions">
                        <button className="btn btn-secondary" onClick={() => editQuickReply(item)}>Editar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setConfirmDeleteQuickReply(item)}>Excluir</button>
                      </div>
                    </div>
                  ))}
                  {!quickReplies.length && <p className="cfgEmptyText">Nenhuma resposta rápida cadastrada.</p>}
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              5. CRM & FUNIL
          ════════════════════════════════════════════════════ */}
          {is("crm") && (
            <div className="cfgSection">
              {funnelNotice && <div className="cfgNoticeInline">{funnelNotice}</div>}

              <div className="cfgCard">
                <div className="cfgCardHdr">
                  <h3>Etapas do funil</h3>
                  <span className="cfgCardBadge">{funnelStages.length} etapa(s)</span>
                </div>

                <div className="cfgStageList">
                  {funnelStages.map((stage, idx) => (
                    <div key={stage.id} className="cfgStageRow">
                      <div className="cfgStageOrder">
                        <button className="cfgIconBtn" onClick={() => moveFunnelStage(stage, "up")} disabled={idx === 0} title="Mover para cima"><Icon name="arrowUp" size={12} /></button>
                        <span>{idx + 1}</span>
                        <button className="cfgIconBtn" onClick={() => moveFunnelStage(stage, "down")} disabled={idx === funnelStages.length - 1} title="Mover para baixo"><Icon name="arrowDown" size={12} /></button>
                      </div>

                      {editingStageId === stage.id ? (
                        <form className="cfgStageEditForm" onSubmit={(e) => { e.preventDefault(); renameFunnelStage(stage.id, editingStageName); }}>
                          <input value={editingStageName} onChange={(e) => setEditingStageName(e.target.value)} autoFocus required />
                          <button type="submit" className="btn btn-primary btn-sm">Salvar</button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingStageId(null)}>Cancelar</button>
                        </form>
                      ) : (
                        <div className="cfgStageInfo">
                          <strong>{stage.name}</strong>
                          {stage.leads_count > 0 && <span>{stage.leads_count} lead(s)</span>}
                        </div>
                      )}

                      {editingStageId !== stage.id && (
                        <div className="cfgStageActions">
                          <button className="cfgIconBtn" onClick={() => { setEditingStageId(stage.id); setEditingStageName(stage.name); }} title="Renomear"><Icon name="edit" size={13} /></button>
                          <button className="cfgIconBtn danger" onClick={() => deleteFunnelStage(stage.id)} title="Excluir"><Icon name="trash" size={13} /></button>
                        </div>
                      )}
                    </div>
                  ))}
                  {!funnelStages.length && <p className="cfgEmptyText">Nenhuma etapa criada.</p>}
                </div>

                <form onSubmit={createFunnelStage} className="cfgStageAddForm">
                  <input value={newStageName} onChange={(e) => setNewStageName(e.target.value)} placeholder="Nome da nova etapa (ex: Qualificação, Demonstração…)" required />
                  <button type="submit" className="btn btn-primary">Adicionar etapa</button>
                </form>

                <div className="cfgInfoBox" style={{ marginTop: 12 }}>
                  <p><strong>Dica:</strong> Etapas com leads abertos não podem ser excluídas. Mova os leads para outra etapa antes de excluir.</p>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
              6-10. PLACEHOLDERS
          ════════════════════════════════════════════════════ */}
          {is("tags")     && <PlaceholderSection icon="tag"   title="Tags & categorias"   desc="Organize conversas e contatos com tags e categorias personalizadas. Em breve." />}
          {is("horarios") && <PlaceholderSection icon="clock" title="Horários & SLA"       desc="Defina os horários de atendimento, feriados e tempos de resposta por prioridade. Em breve." />}
          {is("safeIA")   && <PlaceholderSection icon="ai"    title="SAFE IA"              desc="Inteligência artificial para sugestões de resposta, resumo de conversas e automação. Em breve." />}
          {is("webhooks") && <PlaceholderSection icon="link"  title="WebHooks & API"       desc="Integre o Safe Zap com sistemas externos usando webhooks e a API REST. Em breve." />}
          {is("plano")    && <PlaceholderSection icon="money" title="Plano & Faturamento"  desc="Gerencie seu plano, histórico de pagamentos e dados de faturamento. Em breve." />}

          {/* ════════════════════════════════════════════════════
              11. SISTEMA & APARÊNCIA
          ════════════════════════════════════════════════════ */}
          {is("importar") && <ImportWhatsAppSection api={api} />}

          {is("sistema") && (
            <div className="cfgSection">
              <ThemesPanel />
              <div style={{ marginTop: 12 }}>
                <AuditPanel api={api} />
              </div>
            </div>
          )}

        </div>
      </div>
      </div>{/* page-v2 */}
    </>
  );
}

// ── Themes / Appearance panel ────────────────────────────────────────────────
function ThemesPanel() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [prefs, setPrefs] = useState(getInitialPrefs);

  function changeTheme(value) { setTheme(value); applyTheme(value); saveTheme(value); }
  function changePref(key, value) { const next = updatePref(key, value); setPrefs(next); }
  function reset() {
    if (!window.confirm("Restaurar todas as preferências de aparência ao padrão?")) return;
    const fresh = resetPrefs(); setPrefs(fresh);
  }

  return (
    <div className="cfgCard themesCard">
      <div className="cfgCardHdr">
        <div>
          <h3>Aparência e tema</h3>
          <p>Personalize cores, espaçamento e comportamento da interface. Salvo apenas no seu navegador.</p>
        </div>
        <button className="btn btn-secondary" onClick={reset} type="button">Restaurar padrão</button>
      </div>

      <div className="themesSection">
        <div className="themesSectionLabel">
          <strong>Modo de cor</strong>
          <span>Escolha entre tema claro ou escuro.</span>
        </div>
        <div className="themesOptionRow">
          {[{ id: "light", label: "Claro", icon: "sun" }, { id: "dark", label: "Escuro", icon: "moon" }].map((opt) => (
            <button key={opt.id} type="button" className={`themesChoice ${theme === opt.id ? "active" : ""}`} onClick={() => changeTheme(opt.id)}>
              <Icon name={opt.icon} size={16} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="themesSection">
        <div className="themesSectionLabel">
          <strong>Cor de destaque</strong>
          <span>Define o accent dos botões, links e elementos ativos.</span>
        </div>
        <div className="themesAccentRow">
          {Object.entries(ACCENT_PRESETS).map(([id, accent]) => (
            <button key={id} type="button" className={`themesAccentSwatch ${prefs.accent === id ? "active" : ""}`} onClick={() => changePref("accent", id)} title={accent.name} style={{ "--swatch-color": accent.c }}>
              <span className="themesAccentDot" style={{ background: accent.c }} />
              <small>{accent.name}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="themesSection">
        <div className="themesSectionLabel">
          <strong>Cantos arredondados</strong>
          <span>Quanto mais arredondado, mais suave a interface.</span>
        </div>
        <div className="themesOptionRow">
          {[{ id: "tight", label: "Quadrado" }, { id: "normal", label: "Padrão" }, { id: "rounded", label: "Arredondado" }].map((opt) => (
            <button key={opt.id} type="button" className={`themesChoice ${prefs.radius === opt.id ? "active" : ""}`} onClick={() => changePref("radius", opt.id)}>
              <span className={`themesRadiusPreview r-${opt.id}`} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="themesSection">
        <div className="themesSectionLabel">
          <strong>Densidade</strong>
          <span>Tamanho de botões, inputs e textos.</span>
        </div>
        <div className="themesOptionRow">
          {[{ id: "compact", label: "Compacta" }, { id: "comfortable", label: "Confortável" }, { id: "spacious", label: "Espaçada" }].map((opt) => (
            <button key={opt.id} type="button" className={`themesChoice ${prefs.density === opt.id ? "active" : ""}`} onClick={() => changePref("density", opt.id)}>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="themesSection">
        <div className="themesSectionLabel">
          <strong>Menu lateral</strong>
          <span>Recolha o menu para mostrar apenas ícones e ganhar mais espaço.</span>
        </div>
        <div className="themesOptionRow">
          {[{ id: "expanded", label: "Expandido", icon: "menu" }, { id: "collapsed", label: "Recolhido", icon: "chevronLeft" }].map((opt) => (
            <button key={opt.id} type="button" className={`themesChoice ${prefs.sidebar === opt.id ? "active" : ""}`} onClick={() => changePref("sidebar", opt.id)}>
              <Icon name={opt.icon} size={14} />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="themesSection">
        <div className="themesSectionLabel">
          <strong>Efeitos visuais</strong>
          <span>Animações e desfoque em modais e overlays.</span>
        </div>
        <div className="themesToggleStack">
          <label className="themesToggleRow">
            <span><strong>Animações</strong><small>Transições suaves em hover e mudanças de estado.</small></span>
            <span className="switch">
              <input type="checkbox" checked={prefs.animations} onChange={(e) => changePref("animations", e.target.checked)} />
              <span />
            </span>
          </label>
          <label className="themesToggleRow">
            <span><strong>Desfoque (blur)</strong><small>Background desfocado em modais e overlays.</small></span>
            <span className="switch">
              <input type="checkbox" checked={prefs.blur} onChange={(e) => changePref("blur", e.target.checked)} />
              <span />
            </span>
          </label>
        </div>
      </div>

      <div className="cfgInfoBox" style={{ marginTop: 4 }}>
        <p><strong>Sobre as preferências:</strong> Todas as opções são salvas localmente no seu navegador e não são compartilhadas com a equipe.</p>
      </div>
    </div>
  );
}

// ── Audit Log Panel ──────────────────────────────────────────────────────────
function AuditPanel({ api }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/audit-logs?limit=200")
      .then((r) => setLogs(ensureArray(r.data)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const ACTION_LABELS = {
    "conversation.assigned": "Atribuição",
    "conversation.status_changed": "Status alterado",
    "conversation.transferred": "Transferência",
  };

  const filtered = filter.trim()
    ? logs.filter((l) => {
        const q = filter.toLowerCase();
        return (l.action || "").includes(q) || (l.user_name || "").toLowerCase().includes(q) || (l.description || "").toLowerCase().includes(q);
      })
    : logs;

  return (
    <div className="cfgCard">
      <div className="cfgCardHdr">
        <div>
          <h3>Logs de auditoria</h3>
          <p>Histórico de ações — atribuições, transferências e mudanças de status.</p>
        </div>
        <span className="cfgCardBadge">{logs.length} evento(s)</span>
      </div>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar por ação, usuário ou descrição…" style={{ marginBottom: 12 }} />
      {loading ? (
        <p className="cfgLoadingText">Carregando logs…</p>
      ) : filtered.length === 0 ? (
        <p className="cfgEmptyText">Nenhum log encontrado.</p>
      ) : (
        <div className="cfgAuditList">
          {filtered.map((l) => (
            <div key={l.id} className="cfgAuditRow">
              <div className="cfgAuditIcon"><Icon name="activity" size={13} /></div>
              <div className="cfgAuditBody">
                <div className="cfgAuditMeta">
                  <strong>{ACTION_LABELS[l.action] || l.action}</strong>
                  {l.entity_type && <span className="cfgAuditEntity">{l.entity_type} #{l.entity_id}</span>}
                </div>
                <span>{l.description || l.action}</span>
                <small>por {l.user_name || "sistema"} · {formatTime(l.created_at)}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
